// LogInForm.jsx — complete file with requested changes applied

import React, { useState } from 'react';
import { validateEmailFormat } from '@utils/Index';
import { useAuth } from '@context/AuthContext'; // Import the custom hook
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '@root/firebase'; // Firestore instance
import { getDoc, updateDoc, doc, serverTimestamp } from 'firebase/firestore';
import {
  signInWithEmailAndPassword,
  getMultiFactorResolver,
} from 'firebase/auth';

const LogInForm = ({ onSwitchForm }) => {
  // ⬇️ include setGlobalData so we can hydrate context before navigating
  const { logout, setGlobalData } = useAuth();

  const logo =
    'https://firebasestorage.googleapis.com/v0/b/smartdog-bbe9f.firebasestorage.app/o/images%2Flogo-full.jpg?alt=media&token=685d3267-d81d-4256-b88c-f776c0e08363';

  const navigate = useNavigate(); // useNavigate hook for redirection

  // State for email and its validation
  const [email, setEmail] = useState('');
  const [isEmailValid, setIsEmailValid] = useState(true);

  // State for password and its validation criteria
  const [password, setPassword] = useState('');

  // Form validity state
  const [isFormValid, setIsFormValid] = useState(false);

  // Tooltip states
  const [showInvalidEmailTooltip, setShowInvalidEmailTooltip] = useState(false);

  const [isAuthenticating, setIsAuthenticating] = useState(false);

  /**************************************************************************************** 
    Validate email input and check form is valid
  ****************************************************************************************/
  const handleEmailChange = async (email) => {
    setEmail(email);

    // Validate email format
    const isValid = validateEmailFormat(email);
    setIsEmailValid(isValid);
    setShowInvalidEmailTooltip(!isValid);

    // Update form validity
    setIsFormValid(isValid && password.trim() !== '');
  };

  /**************************************************************************************** 
    Handle Password change and check form is valid
  ****************************************************************************************/
  const handlePasswordChange = (password) => {
    setPassword(password);

    // Update form validity
    setIsFormValid(isEmailValid && password.trim() !== '');
  };

  /**************************************************************************************** 
    Authenticate User Login (emailVerified via Firebase Auth, status via Firestore)
  ****************************************************************************************/
  async function handleAuthenticate() {
    if (!isFormValid) {
      toast.error('Please provide a valid email and password.');
      return;
    }

    try {
      setIsAuthenticating(true);

      // 1) Try to sign in with Auth
      const cred = await signInWithEmailAndPassword(
        auth,
        email.trim(),
        password
      );

      console.log('+++++++ Logging In Start +++++++\n');
      if (!cred.user?.uid) {
        console.error('Error Login, failed to retrieve UID');
        return null;
      }

      console.log('Logged In UID: ', cred.user.uid);
      console.log('Logged In User: ', cred.user.email);
      console.log('Email Verified (Auth): ', cred.user.emailVerified);

      // 2) Hard gate on Auth email verification (source of truth)
      //if (!cred.user.emailVerified) {
      //  toast.error('Please verify your email before logging in.');
      //  await auth.signOut(); // avoid half-authenticated state/guard races
      //  return null;
      //}

      // 3) Get User saved data from Firestore
      const ref = doc(db, 'users', cred.user.uid);
      const snap = await getDoc(ref);
      if (!snap.exists()) {
        console.warn('User profile missing for', cred.user.uid);
        toast.error('Account record missing. Please contact support.');
        await auth.signOut();
        return null;
      }
      const data = snap.data();

      // 4) Optional activation/status check (treat missing as active)
      const status = (data.status || 'active').toLowerCase();
      if (status !== 'active') {
        toast.error(
          status === 'pending'
            ? 'Your account is pending activation. We’ll notify you once it’s ready.'
            : 'Your account is not active. Please contact support.'
        );
        await auth.signOut();
        return null;
      }

      // 5) Best-effort: update lastLoggedIn
      try {
        await updateDoc(ref, { lastLoggedIn: serverTimestamp() });
        console.log('Last Logged in updated (serverTimestamp).');
      } catch (e) {
        console.error('lastLoggedIn update failed:', e?.message || e);
      }

      // 6) Pre-hydrate context BEFORE navigating to avoid route-guard race
      try {
        setGlobalData((prev) => ({ ...(prev || {}), ...data }));
      } catch (e) {
        console.warn('setGlobalData pre-hydration failed (non-fatal):', e);
      }

      // 7) Navigate using an absolute path (and replace history)
      try {
        navigate('/app/dashboard', { replace: true });
        console.log('Navigating to /app/dashboard');
      } catch (e) {
        console.error('Navigate Error:', e);
      }
    } catch (err) {
      // Handle MFA-required flow
      if (err?.code === 'auth/multi-factor-auth-required') {
        try {
          const resolver = getMultiFactorResolver(auth, err);
          const hints = resolver.hints || [];
          onSwitchForm('mfa', { resolver, email, hints });
          return;
        } catch (e2) {
          console.error('MFA error:', e2);
          toast.error('Multi-factor login failed. Try again.');
        }
      } else {
        console.error('Authentication error:', err?.code, err?.message);
        // friendlier errors
        const friendly =
          err?.code === 'auth/wrong-password'
            ? 'Incorrect password.'
            : err?.code === 'auth/user-not-found'
            ? 'No account found for this email.'
            : err?.code === 'auth/too-many-requests'
            ? 'Too many attempts. Try again later.'
            : 'Invalid email or password';
        toast.error(friendly);
      }
    } finally {
      setIsAuthenticating(false);
    }
  }

  async function handleLogOut() {
    await logout();
    toast.success('Logout Successfully!');
  }

  return (
    <div className='authentication-bg min-vh-100'>
      <div className='bg-overlay bg-light'></div>
      <div className='container'>
        <div className='d-flex flex-column min-vh-100 px-3 pt-4'>
          <div className='row justify-content-center my-auto'>
            <div className='col-md-8 col-lg-6 col-xl-5'>
              <div className='card'>
                <div className='card-body p-4'>
                  <div className='text-center mt-2'>
                    <img src={logo} alt='' width='300' className='mb-3' />
                    <h5>Welcome to Smardog!</h5>
                    <p className='text-muted'>
                      Sign in to continue to SmartDog.
                    </p>
                  </div>
                  <div className='p-2 mt-4'>
                    {/* Email Input */}
                    <div className='form-floating mb-3 position-relative'>
                      <input
                        type='email'
                        className='form-control'
                        placeholder='Email'
                        value={email}
                        onChange={(e) => handleEmailChange(e.target.value)}
                      />
                      <label className='form-label'>Email</label>
                      {/* Tooltip for email validation */}
                      {showInvalidEmailTooltip && !isEmailValid && (
                        <div
                          className='invalid-overlay'
                          style={{
                            position: 'absolute',
                            top: '100%',
                            left: '20%',
                            backgroundColor: '#FDF3D7',
                            boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
                            padding: '5px 15px',
                            zIndex: 10,
                            width: 'auto',
                            borderRadius: '9999px',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          <ul className='list-unstyled mb-0'>
                            <li
                              className='requirements'
                              style={{ color: '#927422' }}
                            >
                              <i className='fas fa-times text-warning me-3'></i>
                              Invalid email
                            </li>
                          </ul>
                        </div>
                      )}
                    </div>
                    {/* Password Input */}
                    <div className='form-floating mb-3'>
                      <input
                        type='password'
                        className='form-control'
                        placeholder='Password'
                        value={password}
                        onChange={(e) => handlePasswordChange(e.target.value)}
                      />
                      <label className='form-label'>Password</label>
                    </div>

                    {/* Login Button */}
                    <div className='mt-3'>
                      <button
                        onClick={handleAuthenticate}
                        className='btn btn-primary w-100'
                        disabled={!isFormValid}
                      >
                        {isAuthenticating ? 'Logging In...' : 'Log In'}
                      </button>
                    </div>
                    {/* Forgot password and Register links */}
                    <div className='mt-4 text-center'>
                      <p>
                        Forgot your password?{' '}
                        <a
                          href='#'
                          onClick={() => onSwitchForm('forgot')}
                          className='fw-medium text-primary'
                        >
                          Reset here
                        </a>
                      </p>
                      <p>
                        Don't have an account?{' '}
                        <a
                          href='#'
                          onClick={() => onSwitchForm('register')}
                          className='fw-medium text-primary'
                        >
                          Register
                        </a>
                      </p>
                      <p>
                        Not Logged Out?{' '}
                        <a
                          href='#'
                          onClick={handleLogOut}
                          className='fw-medium text-primary'
                        >
                          Logout
                        </a>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className='row'>
            <div className='col-lg-12'>
              <div className='text-center p-4'>
                <p>
                  Copyright © Smart Dog - All Rights Reserved -
                  <a
                    href='https://www.sis-smartdog.com'
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    www.sis-smartdog.com
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LogInForm;
